import React from 'react';
import { Link } from '../router';
import { useAuth } from '../auth';
import { SITE } from '../config';
import { SiteConfig } from '../types/problem';
import Icon, { IconName } from '../components/Icon';
import { PricingCards, SiteLayout } from '../components/Site';

const STEPS = [
  { title: 'Solve on LeetCode', text: 'Keep practising the way you already do. Nothing changes in your workflow.' },
  { title: 'Save the key idea', text: 'When you get "Accepted", the extension asks for the core approach in one or two lines.' },
  { title: 'Recall on schedule', text: 'Each day CodeReviser picks what to review. Explain the approach, and AI checks it against your notes.' },
];

const FEATURES: { icon: IconName; title: string; text: string }[] = [
  { icon: 'zap', title: 'Captured automatically', text: 'The extension notices accepted submissions and prompts you to save, so nothing slips through.' },
  { icon: 'calendar', title: 'A sensible daily queue', text: 'Overdue and new problems come first, and the backlog is spread out so sessions stay short.' },
  { icon: 'sparkle', title: 'AI-checked recall', text: 'Describe the approach in your own words. You get a clear verdict and a one-line explanation.' },
  { icon: 'repeat', title: 'Misses come back tomorrow', text: 'Recalled problems wait longer each time. Missed ones return the next day until they stick.' },
  { icon: 'clock', title: 'Timed like an interview', text: 'Five minutes per problem builds the habit of retrieving an approach quickly.' },
  { icon: 'shield', title: 'Your data, portable', text: 'Export everything to JSON whenever you like, and import it back without losing history.' },
];

const FAQ = [
  {
    q: 'Is CodeReviser really free?',
    a: (c: SiteConfig) => {
      const plus = c.plans.find(p => p.tier === 'plus');
      return `Yes. Saving problems, the daily review queue, the extension, self-graded reviews and export are free forever. Free accounts also get ${c.freeMonthlyGrades} AI-graded answers each month. ${plus ? `Plus raises that to ${plus.monthlyGrades}, and ` : ''}Pro removes the limit.`;
    },
  },
  {
    q: 'Do I need to paste my code?',
    a: () => "No. You save the idea behind the solution, like “sliding window with a hash map of last-seen indices”. That's what you need to remember in an interview.",
  },
  {
    q: 'Which browsers does the extension support?',
    a: () => 'Chrome and other Chromium browsers such as Edge, Brave and Arc.',
  },
  {
    q: 'Can I cancel anytime?',
    a: () => 'Yes. Cancel from Settings and you keep your plan until the end of the period you paid for. You will not be charged again.',
  },
];

function ProductMock() {
  return (
    <div className="mock" aria-hidden="true">
      <div className="mock-window">
        <div className="mock-chrome">
          <span className="mock-dots"><i /><i /><i /></span>
          <span className="mock-address">{SITE.name} · Daily review</span>
        </div>
        <div className="mock-screen">
          <div className="card">
            <div className="card-body">
              <div className="problem-head">
                <div>
                  <div className="eyebrow">Problem 3 of 5</div>
                  <div className="problem-title" style={{ fontSize: 18 }}>Longest Substring Without Repeating Characters</div>
                </div>
                <div className="timer"><Icon name="clock" size={14} />3:42</div>
              </div>
              <div className="verdict verdict-success" style={{ margin: '18px 0 14px' }}>
                <span className="verdict-icon"><Icon name="check" size={15} /></span>
                <div>
                  <div className="verdict-title">Recalled</div>
                  <div className="verdict-text">You named the sliding window and the last-seen index map, which is the key insight.</div>
                </div>
              </div>
              <div className="compare">
                <div className="panel">
                  <div className="panel-label"><span className="dot accent" />Your answer</div>
                  <div className="panel-body">Sliding window, map char → last index, jump left past repeats.</div>
                </div>
                <div className="panel">
                  <div className="panel-label"><span className="dot success" />Saved approach</div>
                  <div className="panel-body">Two pointers + hash map of last seen positions.</div>
                </div>
              </div>
            </div>
            <div className="card-footer">
              <span className="next-hint">Back in 8 days</span>
              <span className="spacer" />
              <span className="btn btn-primary btn-sm">Mark recalled <Icon name="arrowRight" size={14} /></span>
            </div>
          </div>
            </div>
      </div>
      <div className="card mock-float">
        <div className="list-title">Today's queue</div>
        <div className="list-meta">5 problems · about 25 minutes</div>
      </div>
    </div>
  );
}

export default function Landing() {
  const { me, config } = useAuth();
  const authed = me?.authenticated;

  return (
    <SiteLayout>
      <section className="hero">
        <div className="container hero-grid">
          <div>
            <span className="pill"><Icon name="sparkle" size={14} /> Spaced repetition for LeetCode</span>
            <h1 className="hero-title">Stop forgetting the problems <em>you've already solved.</em></h1>
            <p className="hero-sub">
              CodeReviser saves the key idea behind every accepted solution and brings it back right before you'd
              forget it, so you remember the patterns when the interview comes.
            </p>
            <div className="hero-ctas">
              <Link to={authed ? '/app' : '/signup'} className="btn btn-primary btn-lg">
                {authed ? 'Open your dashboard' : 'Get started free'} <Icon name="arrowRight" size={16} />
              </Link>
              <Link to="/extension" className="btn btn-secondary btn-lg">
                <Icon name="download" size={16} /> Download extension
              </Link>
            </div>
            <div className="hero-note">
              <span><Icon name="check" size={14} /> Free forever plan</span>
              <span><Icon name="check" size={14} /> No card required</span>
              <span><Icon name="check" size={14} /> Works with Chrome</span>
            </div>
          </div>
          <ProductMock />
        </div>
      </section>

      <section className="section section-muted" id="how-it-works">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">How it works</div>
            <h2 className="section-title">Three steps, about five minutes a problem</h2>
          </div>
          <div className="steps">
            {STEPS.map((s, i) => (
              <div className="card step" key={s.title}>
                <div className="step-num">{i + 1}</div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" id="features">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Features</div>
            <h2 className="section-title">Built for remembering, not just solving</h2>
            <p className="section-sub">Solving a problem once doesn't mean you'll recall it in three weeks. Spaced recall does.</p>
          </div>
          <div className="features">
            {FEATURES.map(f => (
              <div className="feature" key={f.title}>
                <div className="feature-icon"><Icon name={f.icon} size={18} /></div>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-muted" id="pricing">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Pricing</div>
            <h2 className="section-title">Free for everyone. Upgrade when you want more AI grading.</h2>
          </div>
          <PricingCards />
        </div>
      </section>

      <section className="section" id="extension">
        <div className="container">
          <div className="card ext-band">
            <div>
              <div className="eyebrow">Chrome extension</div>
              <h2 className="section-title" style={{ marginBottom: 12 }}>Save problems the moment you solve them</h2>
              <p className="section-sub" style={{ marginTop: 0 }}>
                The extension spots an accepted submission on LeetCode and asks for your approach. It saves straight to
                your account, so your dashboard is always up to date.
              </p>
              <div className="hero-ctas" style={{ marginTop: 24 }}>
                {SITE.chromeStoreUrl ? (
                  <a href={SITE.chromeStoreUrl} className="btn btn-primary btn-lg" target="_blank" rel="noopener noreferrer">
                    <Icon name="puzzle" size={16} /> Add to Chrome
                  </a>
                ) : (
                  <a href={SITE.extensionZip} className="btn btn-primary btn-lg" download>
                    <Icon name="download" size={16} /> Download for Chrome
                  </a>
                )}
                <Link to="/extension" className="btn btn-ghost btn-lg">Installation guide</Link>
              </div>
            </div>
            <ol className="ext-steps">
              <li><span><strong>Install the extension</strong> and pin it to your toolbar.</span></li>
              <li><span><strong>Sign in</strong> from the extension popup. It uses your CodeReviser account.</span></li>
              <li><span><strong>Solve a problem.</strong> When it's accepted, save the key idea in one click.</span></li>
            </ol>
          </div>
        </div>
      </section>

      <section className="section section-muted" id="faq">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">FAQ</div>
            <h2 className="section-title">Questions, answered</h2>
          </div>
          <div className="faq">
            {FAQ.map(item => (
              <details key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a(config!)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="card cta">
            <h2 className="section-title">Make your next interview a recall, not a rediscovery</h2>
            <p className="section-sub">Start free in under a minute.</p>
            <div className="hero-ctas" style={{ justifyContent: 'center' }}>
              <Link to={authed ? '/app' : '/signup'} className="btn btn-primary btn-lg">
                {authed ? 'Open your dashboard' : 'Create your free account'}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
